import {Component} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup} from '@angular/forms';
import {UserFilter} from '../../../interface/user-filter.interface';
import {Inject} from '@angular/core';

const SUBSCRIPTION_OPTIONS = ['Starter', 'Standard', 'Premium', 'Basic', 'Professional', 'Unlim'];

@Component({
  selector: 'app-users-filter',
  templateUrl: './users-filter.component.html',
  styleUrls: ['./users-filter.component.scss']
})
export class UsersFilterComponent {

  form: FormGroup;
  subscriptionOptions = SUBSCRIPTION_OPTIONS;

  constructor(
    private dialogRef: MatDialogRef<UsersFilterComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) private data: UserFilter | null
  ) {
    this.form = this.fb.group({
      dateRange: this.fb.group({
        startDate: [null],
        endDate: [null]
      }),
      individualPromoCode: [''],
      teamPromoCode: [''],
      individualSubscription: [''],
      teamSubscription: [''],
      isBusiness: [''],
      hasAdditional: ['']
    });

    this.patchInitialFilter(this.data);
  }

  apply(): void {
    const raw = this.form.value;
    const filter: UserFilter = {};
    const startDate = raw.dateRange?.startDate;
    const endDate = raw.dateRange?.endDate;

    if (startDate) filter.dateFrom = this.formatDate(startDate);
    if (endDate)   filter.dateTo   = this.formatDate(endDate);
    if (raw.individualPromoCode?.trim()) filter.individualPromoCode = raw.individualPromoCode.trim();
    if (raw.teamPromoCode?.trim())       filter.teamPromoCode       = raw.teamPromoCode.trim();
    if (raw.individualSubscription)      filter.individualSubscription = raw.individualSubscription;
    if (raw.teamSubscription)            filter.teamSubscription       = raw.teamSubscription;
    if (raw.isBusiness !== '')  filter.isBusiness  = raw.isBusiness  === 'true';
    if (raw.hasAdditional !== '') filter.hasAdditional = raw.hasAdditional === 'true';

    this.dialogRef.close(filter);
  }

  reset(): void {
    this.form.reset({
      dateRange: {
        startDate: null,
        endDate: null
      },
      individualPromoCode: '',
      teamPromoCode: '',
      individualSubscription: '',
      teamSubscription: '',
      isBusiness: '',
      hasAdditional: ''
    });
    this.dialogRef.close(null);
  }

  close(): void {
    this.dialogRef.close(undefined);
  }

  openRangePicker(picker: any): void {
    picker.open();
  }

  hasDateRange(): boolean {
    const startDate = this.form.get('dateRange.startDate')?.value;
    const endDate = this.form.get('dateRange.endDate')?.value;
    return !!(startDate || endDate);
  }

  getDateRangeLabel(): string {
    const startDate = this.form.get('dateRange.startDate')?.value;
    const endDate = this.form.get('dateRange.endDate')?.value;

    if (!startDate && !endDate) {
      return 'Date';
    }
    if (startDate && endDate) {
      return `${this.formatDisplayDate(startDate)} - ${this.formatDisplayDate(endDate)}`;
    }
    return this.formatDisplayDate(startDate || endDate);
  }

  private formatDate(value: Date | string): string {
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return value;
  }

  private formatDisplayDate(value: Date | string): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  private patchInitialFilter(filter: UserFilter | null): void {
    if (!filter) {
      return;
    }

    this.form.patchValue({
      dateRange: {
        startDate: this.parseDateValue(filter.dateFrom),
        endDate: this.parseDateValue(filter.dateTo)
      },
      individualPromoCode: filter.individualPromoCode || '',
      teamPromoCode: filter.teamPromoCode || '',
      individualSubscription: filter.individualSubscription || '',
      teamSubscription: filter.teamSubscription || '',
      isBusiness: filter.isBusiness === undefined ? '' : String(filter.isBusiness),
      hasAdditional: filter.hasAdditional === undefined ? '' : String(filter.hasAdditional)
    });
  }

  private parseDateValue(value?: string): Date | null {
    if (!value) {
      return null;
    }
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }
}
